import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Modal, Button } from "react-bootstrap";
import { useToasts } from "react-toast-notifications";
import printJS from "print-js";
import { useQueryClient } from "react-query";

import classes from "./printInventory.module.scss";

import { createInventoriesFromTags } from "@/redux/actions/inventory.actions";

import { FETCH_INVENTORY_SUCCESS, CHANGE_TAGS_AVAILABLE, CHANGE_SELECT_INVENTORIES } from "@/redux/types/inventory.types";

import api from "@/services/api.service";
import Fetching from "@/components/Fetching/Fetching";
import ModalFetching from "@/components/Fetching/ModalFetching";
import PrintInventoryTag from "./PrintInventoryTag";
import PrintInventoryTagNew from "./PrintInventoryTagNew";
import PrintLotsTagNew from "./PrintLotsTagNew";
import PrintLotsTag from "./PrintLotsTag";
import usePrintInventoryLabel from "@/hooks/printer/zebraPrinter";
import { base64toBlob } from "@/hooks/printer/useBase64";

const FormPrint = ({ showPrint, setShowPrint, page, paginate, defaultUnit, typePrint, typeCurrency }) => {
	const { addToast } = useToasts();
	const queryClient = useQueryClient();
	const { selected, tags_available, lots_available } = useSelector((state) => state.inventory);
	const { estate_id, search } = useSelector((state) => state.auth.filter);
	const { customer } = useSelector((state) => state.auth.user);

	const { getZpl, printInventoryLabel, isLoading } = usePrintInventoryLabel();

	const dispatch = useDispatch();
	const [fetching, setFetching] = useState(false);
	const [loading, setLoading] = useState(false);

	const [selectLots, setSelectLots] = useState([]);

	useEffect(() => {
		if (showPrint && typePrint === "selected_lot") {
			if (selectLots.length !== 0 && selected.length === 0) {
				setSelectLots([]);
			}

			if (selectLots.length === 0 && selected.length > 0) {
				setFetching(true);
				api.post("/client/print/lots", selected).then((data) => {
					setSelectLots(data.lots);
					setFetching(false);
				});
			}
		}

		if (showPrint && typePrint === "selected") {
			setFetching(true);
			api.post("/client/print/inventories", selected).then((data) => {
				dispatch({
					type: CHANGE_SELECT_INVENTORIES,
					payload: {
						selected: data.inventories,
					},
				});
				setFetching(false);
			});
		}

		// eslint-disable-next-line
	}, [showPrint, typePrint]);

	let ids = [];
	let original_ids = [];
	let inventories = [];
	let quantity = 0;
	let first = "";
	let last = "";
	let product_id = "";

	selected.forEach((element, i) => {
		if (quantity === 0) {
			first = element.code;
			product_id = element.product_id;
		}

		const next_product = selected[i + 1] ? selected[i + 1].product_id : "";

		if (product_id === element.product_id) {
			if (quantity < 350) {
				ids.push(element.original_id);
				original_ids.push(element.original_id);
				last = element.code;
				quantity++;
			}

			if (quantity === 350 || (quantity < 350 && next_product !== product_id)) {
				let newElement = { ...element };
				newElement["quantity"] = quantity;
				newElement["ids"] = ids;
				newElement["original_ids"] = original_ids;
				newElement["first"] = first;
				newElement["last"] = last;
				delete newElement.original_id;
				product_id = element.product_id;
				quantity = 0;
				ids = [];
				inventories.push(newElement);
			}
		}
	});

	async function dispatchInventories(selectedTags) {
		setLoading(true);

		const response_create = await dispatch(createInventoriesFromTags(selectedTags, search, page, paginate, defaultUnit, estate_id, typeCurrency));

		if (customer.zpl_printer_configured) {
			let zplList = [];
			response_create.inventories_print.forEach((item) => {
				const zpl = getZpl({
					productName: item.tradename,
					content: item.content,
					unit_measurement: item.unit_measurement,
					id: item.inventory_id,
					code: item.code,
					expirationDate: item.expirationDate ? item.expirationDate : "",
					businessName: item.businessName ? item.businessName : "",
					lot: item.lot ? item.lot : "",
				});
				zplList.push(zpl);
			});

			await printInventoryLabel(zplList);
		} else {
			var ids = response_create && response_create.inventories_print.map((print) => print.id);
			dispatchPrintTag(ids);
		}

		queryClient.setQueriesData(["purchase_orders_sale"], (data) => {
			let newData = { ...data };
			if (Object.prototype.hasOwnProperty.call(newData, "tags_available")) {
				newData.tags_available = response_create.tags_available;
				newData.lots_available = response_create.lots_available;
			}
			return newData;
		});

		queryClient.setQueriesData(["purchase_orders_delivery"], (data) => {
			let newData = { ...data };
			if (Object.prototype.hasOwnProperty.call(newData, "tags_available")) {
				newData.tags_available = response_create.tags_available;
				newData.lots_available = response_create.lots_available;
			}
			return newData;
		});

		dispatch({
			type: CHANGE_TAGS_AVAILABLE,
			payload: {
				tags_available: response_create.tags_available,
				lots_available: response_create.lots_available,
			},
		});

		return true;
	}

	async function dispatchPrintTag(ids) {
		setLoading(true);
		setFetching(true);

		const response = await api
			.post("/client/inventories/create_tags_pdf", {
				ids,
				defaultUnit,
			})
			.then((data) => {
				setLoading(false);
				setFetching(false);

				return data;
			});

		const blobUrl = URL.createObjectURL(base64toBlob(`data:application/pdf;base64,${response.url}`));

		printJS(blobUrl);

		return true;
	}

	async function dispatchPrintSelectLot(selected) {
		setLoading(true);
		setFetching(true);

		const response = await api
			.post("/client/inventories/create_tags_lots_pdf", {
				selected,
			})
			.then((data) => {
				setLoading(false);
				setFetching(false);

				return data;
			});

		const blobUrl = URL.createObjectURL(base64toBlob(`data:application/pdf;base64,${response.url}`));

		printJS(blobUrl);

		return true;
	}

	async function dispatchPrintLots(lots_available) {
		setLoading(true);
		setFetching(true);

		await api
			.post("/client/inventories/create_from_lots", {
				search,
				page,
				paginate,
				defaultUnit,
				estate_id,
				lots_available,
			})
			.then((response) => {
				dispatch({
					type: FETCH_INVENTORY_SUCCESS,
					payload: {
						inventories: response.data.inventories.data,
						products: response.data.products,
						dealers: response.data.dealers,
						quantity: response.data.inventories.total,
						last_page: response.data.inventories.last_page,
						from: response.data.inventories.from,
						to: response.data.inventories.to,
						permissions: response.data.permissions,
					},
				});

				dispatch({
					type: CHANGE_TAGS_AVAILABLE,
					payload: {
						tags_available: response.data.tags_available,
						lots_available: response.data.lots_available,
					},
				});

				setLoading(false);
				setFetching(false);
			});

		dispatchPrintSelectLot(lots_available);

		return true;
	}

	const handlePrint = async (type_print) => {
		if (type_print === "newtags") {
			if (tags_available.reduce((total, item) => total + item.quantity_available, 0) > 350) {
				addToast("Se deben imprimir un máximo de 350 etiquetas.", {
					appearance: "error",
				});
				return false;
			}

			dispatchInventories(tags_available);
		} else if (type_print === "lots") {
			if (lots_available.length > 350) {
				addToast("Se deben imprimir un máximo de 350 etiquetas.", {
					appearance: "error",
				});
				return false;
			}

			dispatchPrintLots(lots_available);
		} else if (type_print === "select_lots") {
			if (selectLots.length > 350) {
				addToast("Se deben imprimir un máximo de 350 etiquetas.", {
					appearance: "error",
				});
				return false;
			}

			dispatchPrintSelectLot(selectLots);
		} else {
			var ids = [];
			var quantity = 0;

			inventories.forEach(function (print) {
				ids = [...ids, ...print.original_ids];
				quantity += print.ids.length;
			});

			if (quantity > 350) {
				addToast("Se deben imprimir un máximo de 350 etiquetas.", {
					appearance: "error",
				});
				return false;
			}
			if (customer.zpl_printer_configured) {
				let zplList = [];
				selected.forEach((inventory) => {
					const zpl = getZpl({
						productName: inventory.tradename,
						id: inventory.original_id,
						code: inventory.code,
						content: inventory.container_content,
						unit_measurement: inventory.unit_measurement,
						lot: inventory.lot_code ? inventory.lot_code : "",
						expirationDate: inventory.expiration_date ? inventory.expiration_date : "",
						businessName: inventory.business_name ? inventory.business_name : "",
					});
					zplList.push(zpl);
				});
				await printInventoryLabel(zplList);
			} else {
				dispatchPrintTag(ids);
			}
		}

		queryClient.removeQueries(["purchase_orders_sale"]);

		closeModal();
	};

	const handlePrintRowSelected = async (tag, _i) => {
		closeModal();
		if (customer.zpl_printer_configured) {
			let zplList = [];
			selected.forEach((inventory) => {
				const zpl = getZpl({
					productName: inventory.tradename,
					id: inventory.original_id,
					code: inventory.code,
					content: inventory.container_content,
					unit_measurement: inventory.unit_measurement,
					lot: inventory.lot_code ? inventory.lot_code : "",
					expirationDate: inventory.expiration_date ? inventory.expiration_date : "",
					businessName: inventory.business_name ? inventory.business_name : "",
				});
				zplList.push(zpl);
			});
			await printInventoryLabel(zplList);
		} else {
			dispatchPrintTag(tag.original_ids);
		}
	};

	const handlePrintRow = (tag, _i) => {
		closeModal();

		const tagsArray = [];
		tagsArray.push(tag);

		dispatchInventories(tagsArray);
	};

	const handlePrintRowLot = (lot, _i) => {
		closeModal();

		const tagsArray = [];
		tagsArray.push(lot);

		dispatchPrintLots(tagsArray);
	};

	const closeModal = () => {
		dispatch({
			type: CHANGE_SELECT_INVENTORIES,
			payload: {
				selected: [],
			},
		});
		setSelectLots([]);
		setShowPrint(false);
	};

	return (
		<>
			<Modal show={showPrint} onHide={closeModal} backdrop="static" keyboard={false} size="xl" id="container_print_tag">
				<Modal.Header>
					<Modal.Title>Imprimir Etiquetas</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					{fetching ? (
						<Fetching topMargin={"2em"} bottomMargin={"2em"} />
					) : (
						<>
							{typePrint === "new" && <PrintInventoryTag handlePrint={handlePrint} handlePrintRow={handlePrintRow} />}

							{typePrint === "selected" && (
								<PrintInventoryTagNew inventories={inventories} handlePrint={handlePrint} handlePrintRowSelected={handlePrintRowSelected} />
							)}

							{typePrint === "lots" && <PrintLotsTagNew handlePrint={handlePrint} handlePrintRowLot={handlePrintRowLot} />}

							{typePrint === "selected_lot" && <PrintLotsTag selectLots={selectLots} handlePrint={handlePrint} />}
						</>
					)}
				</Modal.Body>
				<Modal.Footer>
					<Button className={classes.buttonForm} variant="danger" onClick={closeModal}>
						CERRAR
					</Button>
				</Modal.Footer>
			</Modal>

			<ModalFetching text={`Generando documento, esto puede tardar unos minutos.`} loading={loading || isLoading} setLoading={setLoading} />
		</>
	);
};

export default FormPrint;
